import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useInRouterContext, useLocation } from "react-router-dom";
import "../styles/tokens.css";
import "../styles/components.css";
import AppToast from "./AppToast";

const VARIANT_DURATIONS = {
  success: 3000,
  info: 3000,
  warning: 4000,
  error: 5000,
};
const TOAST_TRANSITION_DURATION = 180;

const AppToastContext = createContext(null);

function normalizeToastInput(input) {
  if (typeof input === "string") {
    return { title: input };
  }

  if (input && typeof input === "object") {
    return { title: input.title || "" };
  }

  return { title: "" };
}

function AppToastRouteReset({ clear, enabled }) {
  const location = useLocation();

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    clear();
    return undefined;
  }, [clear, enabled, location.key]);

  return null;
}

export function AppToastProvider({
  children,
  position = "top-center",
  maxVisible = 3,
  clearOnRouteChange = true,
}) {
  const [toasts, setToasts] = useState([]);
  const nextIdRef = useRef(0);
  const toastsRef = useRef([]);
  const timeoutsRef = useRef(new Map());
  const isInRouter = useInRouterContext();

  useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  const getToastTransitionDuration = useCallback(() => {
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return 0;
    }

    return TOAST_TRANSITION_DURATION;
  }, []);

  const scheduleToastTimer = useCallback((toastId, callback, delay) => {
    const timeoutId = timeoutsRef.current.get(toastId);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }

    const nextTimeoutId = window.setTimeout(() => {
      timeoutsRef.current.delete(toastId);
      callback();
    }, delay);

    timeoutsRef.current.set(toastId, nextTimeoutId);
  }, []);

  const removeToast = useCallback((toastId) => {
    timeoutsRef.current.delete(toastId);
    setToasts((current) => current.filter((toast) => toast.id !== toastId));
  }, []);

  const dismiss = useCallback(
    (toastId) => {
      const toast = toastsRef.current.find((entry) => entry.id === toastId);
      if (!toast || toast.exiting) {
        return;
      }

      setToasts((current) =>
        current.map((entry) => (entry.id === toastId ? { ...entry, exiting: true } : entry))
      );

      scheduleToastTimer(toastId, () => removeToast(toastId), getToastTransitionDuration());
    },
    [getToastTransitionDuration, removeToast, scheduleToastTimer]
  );

  const clear = useCallback(() => {
    const activeToastIds = toastsRef.current.filter((toast) => !toast.exiting).map((toast) => toast.id);
    if (!activeToastIds.length) {
      return;
    }

    setToasts((current) => current.map((toast) => (toast.exiting ? toast : { ...toast, exiting: true })));

    const transitionDuration = getToastTransitionDuration();
    activeToastIds.forEach((toastId) => {
      scheduleToastTimer(toastId, () => removeToast(toastId), transitionDuration);
    });
  }, [getToastTransitionDuration, removeToast, scheduleToastTimer]);

  const push = useCallback(
    (variant, input, options = {}) => {
      const normalized = normalizeToastInput(input);
      const title = String(normalized.title || "").trim();

      if (!title) {
        return null;
      }

      const duplicateKey = `${variant}::${title}`;
      const currentToasts = toastsRef.current;
      if (currentToasts.some((toast) => toast.duplicateKey === duplicateKey)) {
        return null;
      }

      const activeToasts = currentToasts.filter((toast) => !toast.exiting);
      const overflowToastId = activeToasts.length >= maxVisible ? activeToasts[0]?.id : null;
      // duration: null (oder 0/Infinity) haelt den Toast offen, bis ihn jemand
      // schliesst — fuer Laufmeldungen wie "Plan wird erstellt ...".
      const requestedDuration = options.duration ?? VARIANT_DURATIONS[variant] ?? VARIANT_DURATIONS.info;
      const duration = Number.isFinite(requestedDuration) && requestedDuration > 0
        ? requestedDuration
        : null;
      const nextToast = {
        id: `ds-toast-${nextIdRef.current++}`,
        variant,
        title,
        duplicateKey,
        duration,
        exiting: false,
      };

      setToasts((current) => {
        const nextToasts = overflowToastId
          ? current.map((toast) => (toast.id === overflowToastId && !toast.exiting ? { ...toast, exiting: true } : toast))
          : current;
        return [...nextToasts, nextToast];
      });

      if (overflowToastId) {
        scheduleToastTimer(overflowToastId, () => removeToast(overflowToastId), getToastTransitionDuration());
      }

      if (nextToast.duration !== null) {
        scheduleToastTimer(nextToast.id, () => dismiss(nextToast.id), nextToast.duration);
      }
      return nextToast.id;
    },
    [dismiss, getToastTransitionDuration, maxVisible, removeToast, scheduleToastTimer]
  );

  useEffect(() => {
    const timeouts = timeoutsRef.current;

    return () => {
      timeouts.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      timeouts.clear();
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      show: (input, options) => push(options?.variant || "info", input, options),
      success: (input, options) => push("success", input, options),
      info: (input, options) => push("info", input, options),
      warning: (input, options) => push("warning", input, options),
      error: (input, options) => push("error", input, options),
      dismiss,
      clear,
    }),
    [clear, dismiss, push]
  );

  return (
    <AppToastContext.Provider value={contextValue}>
      {children}
      {isInRouter ? <AppToastRouteReset clear={clear} enabled={clearOnRouteChange} /> : null}
      {typeof document !== "undefined"
        ? createPortal(
            <div className={`ds-toast-stack ds-toast-stack--${position}`} aria-live="polite" aria-atomic="false">
              {toasts.map((toast) => (
                <div
                  key={toast.id}
                  className={`ds-toast-stack__item${toast.exiting ? " ds-toast-stack__item--closing" : ""}`}
                >
                  <AppToast
                    variant={toast.variant}
                    title={toast.title}
                    onClose={() => dismiss(toast.id)}
                  />
                </div>
              ))}
            </div>,
            document.body
          )
        : null}
    </AppToastContext.Provider>
  );
}

export function useAppToast() {
  const context = useContext(AppToastContext);

  if (!context) {
    throw new Error("useAppToast must be used within an AppToastProvider.");
  }

  return context;
}
